import {useCallback, useMemo, useRef, useState} from 'react';

import {photoUrl, usePeople} from '../../../shared/api/usePeople';
import {Avatar} from '../../../shared/components/avatar';
import {PlusIcon} from '../../../shared/components/icons';
import {useDismiss} from '../../../shared/hooks/useDismiss';
import type {EditorPerson} from '../editorState';

const MAX_RESULTS = 6;

// Searches the directory and attaches one person per pick to an option.
export const PersonPicker = ({
    label,
    attached,
    disabled,
    onPick,
}: {
    label: string;
    attached: Set<string>;
    disabled: boolean;
    onPick: (person: EditorPerson) => void;
}) => {
    const root = useRef<HTMLDivElement>(null);
    const directory = usePeople();
    const [open, setOpen] = useState(false);
    const [filter, setFilter] = useState('');
    const close = useCallback(() => {
        setOpen(false);
        setFilter('');
    }, []);
    useDismiss(root, open, close);

    const people = useMemo(
        () =>
            (directory.data?.people ?? []).map((p) => ({
                humaansId: p.humaans_id,
                name: p.name,
                photoUrl: p.has_photo ? photoUrl(p.id) : null,
            })),
        [directory.data],
    );
    const query = filter.trim().toLowerCase();
    const matches = people
        .filter((p) => !attached.has(p.humaansId) && p.name.toLowerCase().includes(query))
        .slice(0, MAX_RESULTS);
    const unavailable = !directory.isPending && !directory.data?.available;

    if (!open) {
        return (
            <button
                type="button"
                className="bt-chip bt-chip--add"
                disabled={disabled}
                aria-label={label}
                onClick={() => setOpen(true)}
            >
                <PlusIcon /> Add person
            </button>
        );
    }

    return (
        <div ref={root} className="bt-picker">
            {unavailable ? (
                <p className="bt-help">Directory unavailable. Type the option title instead.</p>
            ) : (
                <>
                    <input
                        className="bt-field bt-picker__search"
                        placeholder="Search people"
                        aria-label="Search people"
                        // biome-ignore lint/a11y/noAutofocus: the picker opens on an explicit click.
                        autoFocus
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                    />
                    <ul className="bt-picker__list">
                        {matches.map((p) => (
                            <li key={p.humaansId}>
                                <button
                                    type="button"
                                    className="bt-picker__row"
                                    onClick={() => {
                                        onPick(p);
                                        close();
                                    }}
                                >
                                    <Avatar src={p.photoUrl ?? undefined} name={p.name} size={24} />
                                    <span>{p.name}</span>
                                </button>
                            </li>
                        ))}
                        {matches.length === 0 && !directory.isPending && (
                            <li className="bt-help">Nobody matches.</li>
                        )}
                    </ul>
                </>
            )}
        </div>
    );
};
